import {useTranslation} from 'hooks/translations/useCustomTranslation';
import {useBackgroundStyleConfigs} from 'hooks/useBackgroundStyleConfigs';
import {useGetTranslatedCategory} from 'hooks/useGetTranslatedCategory';
import Image from 'next/image';
import {useRouter} from 'next/router';
import React, {FC, useEffect, useState} from 'react';

import {getApiV1Group} from 'lib/api/backend';
import {Group} from 'lib/api/backend.schemas';
import {TypoComponentsGroupInfoSection} from 'lib/api/strapi';

import {Box, Grid, Typography} from 'components/basic-components';
import {ButtonWithConfig} from 'components/basic-components/Button/ButtonWithConfig';
import CDWPopup from 'components/basic-components/CDWPopup/CDWPopup';
import MarkdownContent from 'components/basic-components/MarkdownContent';
import {withErrorBoundary} from 'components/error/ErrorBoundary';
import {VehicleFlags} from 'components/section-components/reservation/Group';

import AdditionalFeatures from './AdditionalFeatures';
import {FeatureContainer} from './Features';
import styles from './GroupsInfoSection.module.scss';

const path_mapping = {de: 'tarife', en: 'rates'};

const GroupInfoSection: FC<TypoComponentsGroupInfoSection> = ({
  image,
  group,
  content,
  features,
  additionalFeatures,
  title,
  titleLevel,
  loadFeatures,
  loadCDWInformation,
  imagePosition,
}) => {
  const {t} = useTranslation(['strapiComponents', 'common']);
  const {locale} = useRouter();

  const [groupData, setGroupData] = useState<Group>();

  const img = image?.data?.attributes?.url;

  const getTranslatedCategory = useGetTranslatedCategory();

  useEffect(() => {
    const getGroup = async () => {
      if (group) {
        const response = await getApiV1Group(group);
        setGroupData(response);
      }
    };
    getGroup();
  }, [group]);

  const {textColor, groupInfoSection} = useBackgroundStyleConfigs();

  const filter = {category: groupData?.category, type: groupData?.types};

  const getBasePath = (locale, pathMapping) =>
    `${locale === 'en' ? '/en' : ''}/${pathMapping?.[locale]}/`;

  const getFilterQuery = filter => `?filter=${encodeURIComponent(JSON.stringify(filter))}`;

  const getPath = (groupData, filter, locale, pathMapping) => {
    const basePath = getBasePath(locale, pathMapping);

    if (!groupData || !filter) {
      return basePath;
    }

    const isPkwCategory = groupData?.category === getTranslatedCategory('pkw');
    const filterQuery = getFilterQuery(filter);

    return isPkwCategory ? `${basePath}${filterQuery}` : `${basePath}${filterQuery}`;
  };

  return (
    <Box sx={{padding: '50px 0', color: textColor}}>
      <Grid
        container
        columnSpacing={'2%'}
        rowSpacing={'20px'}
        sx={{flexDirection: {xxs: 'row', lg: imagePosition === 'left' ? 'row-reverse' : 'row'}}}
      >
        <Grid xxs={12} lg={6}>
          {title ? (
            <Typography variant={'h2'} component={titleLevel} className={styles.title}>
              {title}
            </Typography>
          ) : null}

          <MarkdownContent content={content} className={styles.markdown} />
          <Box
            className={
              features !== null || additionalFeatures.length !== 0 || (loadFeatures && groupData)
                ? null
                : styles.noContent
            }
          >
            <Typography className={styles.featureTitle}>{t('features')}</Typography>
            <FeatureContainer features={features} groupData={loadFeatures ? groupData : {}} />
            <AdditionalFeatures additionalFeatures={additionalFeatures} />
            {loadCDWInformation ? (
              <Box sx={{marginTop: '25px', width: 'fit-content'}}>
                <CDWPopup group={groupData} />
              </Box>
            ) : null}
          </Box>
          <ButtonWithConfig
            variant={groupInfoSection.buttonStyle.variant}
            buttonProps={{
              buttonLink: getPath(groupData, filter, locale, path_mapping),
              buttonText:
                typeof groupData?.bookable === 'boolean' && !groupData?.bookable
                  ? t('groupNotAvailable')
                  : t('toTheVehicles'),
              buttonPosition: 'left',
            }}
            sx={{marginTop: '30px !important'}}
          ></ButtonWithConfig>
        </Grid>
        <Grid xxs={12} lg={6}>
          <div style={{position: 'relative'}}>
            <VehicleFlags
              sx={{
                top: '10px',
                left: imagePosition === 'left' ? '10px' : null,
                right: imagePosition === 'right' ? '10px' : null,
              }}
              group={groupData}
            />
            <Image
              src={img}
              alt=""
              width={image?.data?.attributes?.width}
              height={image?.data?.attributes?.height}
              className={styles.image}
            />
            {!groupData ? null : (
              <Typography
                sx={{
                  left: imagePosition === 'left' ? '10px' : null,
                  right: imagePosition === 'right' ? '10px' : null,
                }}
                className={styles.groupName}
              >
                {t('group', {ns: 'common'})} {groupData?.id}
              </Typography>
            )}
          </div>
        </Grid>
      </Grid>
    </Box>
  );
};

export default withErrorBoundary(GroupInfoSection, ' GroupInfoSection');
